import { Brand } from "../components/Icon";
import type { ForgeRemoteApi } from "../api/forgeRemoteApi";
import { formatDateTime, formatDurationUntil } from "../lib/time";
import { useActivate, useActivationPreview } from "../state/remoteQueries";

const capabilityLabels: Record<string, string> = {
  "projection.read": "Read Crew projections",
  "evidence.read": "Read bounded evidence",
  "manager.chat": "Manager chat",
  "attention.ack": "Acknowledge attention",
  "attention.snooze": "Snooze attention",
  "board.edit": "Edit bounded board fields",
  "decision.respond": "Respond to decisions",
  "run.pause": "Pause Runs",
  "run.resume": "Resume Runs",
};

export function ActivationScreen({ api, onActivated }: { api: ForgeRemoteApi; onActivated: () => void }) {
  const preview = useActivationPreview(api);
  const activate = useActivate(api);

  const handleActivate = async () => {
    try {
      await activate.mutateAsync();
      onActivated();
    } catch {
      // The mutation state renders the retry-safe error without unlocking the projection.
    }
  };

  return (
    <main className="activation-shell">
      <section className="activation-card" aria-labelledby="activation-title">
        <Brand />
        <p className="eyebrow">Remote link · scope preview</p>
        <h1 id="activation-title">Know exactly what this phone can reach.</h1>
        <p className="lede">This device receives a bounded projection. The Forge host remains authoritative and can revoke access at any time.</p>

        {preview.isPending && <div className="loading-block" role="status">Reading the one-time link…</div>}
        {preview.isError && (
          <div className="error-block" role="alert">
            The activation preview could not be loaded.
            <button type="button" className="text-button" onClick={() => preview.refetch()}>Try again</button>
          </div>
        )}
        {preview.data && (
          <>
            <div className="activation-section">
              <span className="section-label">Crews exposed</span>
              <div className="activation-list">
                {preview.data.crews.map((crew) => (
                  <div className="activation-row" key={crew.id}>
                    <strong>{crew.name}</strong>
                    <span className="state-text">{crew.state}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="activation-section">
              <span className="section-label">This link grants</span>
              <div className="capability-list">
                {preview.data.capabilities.map((capability) => <span className="capability-chip" key={capability}>{capabilityLabels[capability] ?? capability}</span>)}
              </div>
              <p className="fine-print">No cancel, stop, code, terminal, connector or member provisioning. Expires {formatDateTime(preview.data.expiresAt)} · {formatDurationUntil(preview.data.expiresAt)}.</p>
            </div>
            <div className="authority-note">
              <strong>{preview.data.hostLabel}</strong>
              <span>{preview.data.source === "fixture-preview" ? "Development fixture · command authority unavailable" : `${preview.data.commandAuthority} command authority`}</span>
            </div>
            <button type="button" className="primary-button activation-button" onClick={handleActivate} disabled={activate.isPending}>
              {activate.isPending ? "Activating this device…" : "Activate this device"}
            </button>
            {activate.isError && <p className="error-copy" role="alert">Activation could not be confirmed. Retry here with this device; the projection remains locked until the session is proven.</p>}
          </>
        )}
      </section>
    </main>
  );
}
